import { Agenda, Box, Button, Input, Table, TopBar } from '@sam/components';
import { TableHeader, TableKey } from '@sam/components/src/Table/Table.types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AbsenceReason,
  Comment,
  getHolidayById,
  Holiday,
  SchambeckUser,
  updateHoliday,
  updateSchambeckUserTableConfig,
  useData,
} from 'shared';
import { IconComponent, IconType } from '../../components/icons/IconComponent';
import { useUser } from '../../components/UserContext';
import { createComment } from '../../utils/comment/Comment.utils';
import { generateAgendaEntries, generateEmptyHoliday } from '../../utils/holiday/Holiday.utils';
import { getLastOvertimeForUser, getUserNameForSimpleUser } from '../../utils/user/User.utils';

export const HolidayRequestDecide: React.FC = () => {
  const { t } = useTranslation();
  const { user, axios, updateContextUser } = useUser();
  const location = useLocation<{ request?: Holiday }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeRequest, setActiveRequest] = useState<Holiday>(
    location.state?.request || generateEmptyHoliday(),
  );
  const [decisionComment, setDecisionComment] = useState<Comment>(
    activeRequest.decisionComment || createComment(user.id, ''),
  );

  const { data: loadedUsers } = useData('SCHAMBECK_USERS_ALL_ACTIVE', {
    config: { fallbackData: [] },
    params: { userId: user.id },
  });

  const { data: loadedHolidays } = useData('HOLIDAYS_ALL', {
    config: { fallbackData: [] },
    params: { userId: user.id },
  });
  const { data: loadedAbsenceReasons } = useData('ABSENCE_REASONS_ALL', {
    config: { fallbackData: [] },
    params: { userId: user.id },
  });

  //Hook to check if an holidayId is provided in the Url and load the entry
  useEffect(() => {
    const searchParam: string | null = searchParams.get('holidayId');
    if (activeRequest || searchParam === null) return;
    getHolidayById(searchParam, axios).then((holiday) => {
      holiday && setActiveRequest(holiday);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hook to set the EntryGroups of the Agenda, after all informations are loaded
  const agendaEntryGroups = useMemo(
    () => generateAgendaEntries(loadedUsers, loadedHolidays),
    [loadedUsers, loadedHolidays],
  );

  /**
   * Helper method to update the holidays on decisions
   * @param approved boolean if the holiday was approved or denied
   */
  const handleDecideHoliday = (approved: boolean): void => {
    updateHoliday(axios, {
      ...activeRequest,
      decisionComment,
      approved,
      denied: !approved,
      decidedBy: user.id,
    }).then(() => navigate('/holiday/edit'));
  };

  /**
   * Holds the display information about the vacationDays or overtime, depending on the request reason
   */
  const saldoInformation: React.JSX.Element = useMemo((): React.JSX.Element => {
    const reason: AbsenceReason | undefined = loadedAbsenceReasons.find(
      (reason) => reason.id === activeRequest.reasonId,
    );
    const requestUser: SchambeckUser | undefined = loadedUsers.find(
      (user) => user.id === activeRequest.userId,
    );
    if (reason?.azkReduction && requestUser) {
      const overTimeSaldoLastMonth: number = getLastOvertimeForUser(requestUser)?.overTimeEnd || 0;
      return (
        <div className="holiday-decide__saldo-wrapper">
          <p className="holiday-decide__saldo-wrapper__content">
            {t('pages.holiday.requestDecision.overTimeStart', {
              replace: { AMOUNT: overTimeSaldoLastMonth },
            })}
          </p>
          <p className="holiday-decide__saldo-wrapper__content">
            {t('pages.holiday.requestDecision.overTimeRequest', {
              replace: { AMOUNT: activeRequest.totalAmount },
            })}
          </p>
        </div>
      );
    } else
      return (
        <div className="holiday-decide__saldo-wrapper">
          <p className="holiday-decide__saldo-wrapper__content">
            {t('pages.holiday.requestDecision.holidayAvailable', {
              replace: { AMOUNT: requestUser?.availableVacationDays || '-' },
            })}
          </p>
          <p className="holiday-decide__saldo-wrapper__content">
            {t('pages.holiday.requestDecision.holidayRequested', {
              replace: { AMOUNT: activeRequest.totalAmount || '-' },
            })}
          </p>
        </div>
      );
  }, [
    activeRequest.reasonId,
    activeRequest.totalAmount,
    activeRequest.userId,
    loadedAbsenceReasons,
    loadedUsers,
    t,
  ]);

  return (
    <>
      <TopBar
        onBackClick={() => navigate('/holiday/edit')}
        title={t('pages.holiday.requestDecision.topBarHeadline')}
      />
      <Box>
        <Agenda entryGroups={agendaEntryGroups} />
      </Box>
      <Box>
        <>
          {saldoInformation}
          <Table
            tableConfig={user.tableConfig?.get(TableKey.HOLIDAY_DECIDE)}
            updateTableConfig={(updatedHeader) => {
              updateSchambeckUserTableConfig(
                axios,
                user,
                TableKey.HOLIDAY_DECIDE,
                updatedHeader,
              ).then((updatedUser) => updatedUser && updateContextUser(updatedUser));
            }}
            header={
              t('pages.holiday.requestDecision.tableHeader', {
                returnObjects: true,
              }) as TableHeader[]
            }
            rows={[
              {
                id: activeRequest.id,
                content: [
                  getUserNameForSimpleUser(activeRequest.userId, loadedUsers),
                  activeRequest.createDate.toLocaleDateString('DE-de'),
                  activeRequest.startDate.toLocaleDateString('DE-de'),
                  activeRequest.endDate.toLocaleDateString('DE-de'),
                  activeRequest.totalAmount,
                  <IconComponent type={IconType.PENDING} width={30} />,
                ],
              },
            ]}
          />
          <div className="holiday-decide__request-comment__wrapper">
            <p className="holiday-decide__request-comment__title">
              {t('pages.holiday.requestDecision.message')}
            </p>
            <p className="holiday-decide__request-comment__content">
              {activeRequest.requestComment?.content}
            </p>
          </div>
          <Input
            type="text"
            value={decisionComment.content}
            label={t('pages.holiday.requestDecision.comment')}
            onChange={(content) => setDecisionComment({ ...decisionComment, content })}
          />
          <div className="holiday-decide__button-wrapper">
            <Button
              onClick={() => handleDecideHoliday(true)}
              value={t('general.buttons.holidayApprove')}
              type="button"
              maxWidth={300}
            />
            <Button
              disabled={!decisionComment.content}
              onClick={() => handleDecideHoliday(false)}
              value={t('general.buttons.holidayDeny')}
              type="button"
              backgroundColor="#BC2E46"
              maxWidth={300}
            />
          </div>
        </>
      </Box>
    </>
  );
};
